// Advanced filter popover — Polish B2.
//
// Replaces the FE-041 simple-presets dropdown with a full builder
// matching the v1 ``TaskFilterPopover`` UX:
//
//   [ Filter (n) ▾ ]
//   ┌── popover ────────────────────────────────────────────────┐
//   │ [New Filter] [Saved Filters]              [Reset] [Save]  │
//   │ ┌─ Group ──────────────────────────┐                      │
//   │ │ Match all (AND)                  │                      │
//   │ │ Where  Field   Operator   Value  │                      │
//   │ │ AND    Field   Operator   Value  │                      │
//   │ │ + Add nested filter              │                      │
//   │ └──────────────────────────────────┘                      │
//   │ Match all (AND)                                            │
//   │ ┌─ Group ──────────────────────────┐                      │
//   │ │ ...                              │                      │
//   │ └──────────────────────────────────┘                      │
//   │ + Add filter                                               │
//   └────────────────────────────────────────────────────────────┘
//
// State is local; the parent owns the URL write via the ``onApply``
// callback (which receives the wire-shape tree). Auto-applies on
// every meaningful change with a 250ms debounce so the URL stays
// in sync without thrashing.

import { ChevronDown, ChevronUp, Filter, Plus } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { FilterGroupCard } from "./FilterGroup";
import { SaveFilterModal } from "./SaveFilterModal";
import { SavedFiltersTab } from "./SavedFiltersTab";
import {
  emptyUiState,
  isComplete,
  makeId,
  uiToWire,
  wireToUi,
  type FilterGroup,
  type FilterUiState,
} from "./treeCodec";
import {
  useCreateSavedFilterMutation,
  useUpdateSavedFilterMutation,
} from "../hooks";
import type { SavedFilterDto, SavedFilterVisibility } from "../api";
import type { FilterTree } from "../urlState";


export type QuickFilterKey =
  | "all_work"
  | "assigned_to_me"
  | "overdue"
  | "private"
  | "archive";


export interface FilterPopoverProps {
  /** Tree currently applied to the page (read from the URL). The
   * popover decodes it into UI state on open / when the URL changes
   * out from under the popover. */
  appliedTree: FilterTree | undefined;
  /** Called whenever the popover commits a tree to the URL. */
  onApply: (tree: FilterTree | undefined) => void;
  /** Toast hook from the parent so save success / failure surfaces
   * outside the popover. */
  pushToast: (text: string, kind?: "success" | "error" | "info") => void;
  /** Quick-filter action — the page handles the actual URL write.
   * Optional so existing callers don't need to pass it; when
   * absent the Quick Filters tab still renders but every action is
   * a no-op. */
  onQuickFilter?: (key: QuickFilterKey) => void;
}


export function FilterPopover({
  appliedTree, onApply, pushToast, onQuickFilter,
}: FilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Click-outside dismisses the panel. Listen in **capture** phase
  // so we read ``event.target`` before React's bubble-phase handlers
  // mutate the DOM (e.g. selecting a value in a MultiSelect re-renders
  // the listbox and detaches the clicked <li>; if we ran in bubble,
  // ``containerRef.current.contains(target)`` would be false because
  // the option is already gone from the tree).
  useEffect(() => {
    if (!open) return;
    function handler(event: MouseEvent) {
      const node = containerRef.current;
      if (
        node && event.target instanceof Node
        && !node.contains(event.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [open]);

  // The condition-count badge needs to render even when the panel is
  // closed; decode the URL tree once for that purpose.
  const conditionCount = useMemo(() => {
    const decoded = wireToUi(appliedTree).state;
    return decoded.groups.reduce(
      (n, g) => n + g.conditions.filter(isComplete).length,
      0,
    );
  }, [appliedTree]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        data-testid="commitments-filter-builder-button"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-raised px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-recessed"
      >
        <Filter
          size={12}
          aria-hidden="true"
          className="text-text-tertiary"
        />
        Filter
        {conditionCount > 0 && (
          <span
            data-testid="commitments-filter-builder-count"
            className="inline-flex items-center justify-center rounded-full bg-teal px-1.5 text-[10px] text-text-inverse"
          >
            {conditionCount}
          </span>
        )}
        {open ? (
          <ChevronUp size={12} aria-hidden="true" className="text-text-tertiary" />
        ) : (
          <ChevronDown size={12} aria-hidden="true" className="text-text-tertiary" />
        )}
      </button>

      {open && (
        <PopoverPanel
          appliedTree={appliedTree}
          onApply={onApply}
          pushToast={pushToast}
          onQuickFilter={onQuickFilter}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}


// Inner panel — only mounts when the user opens the popover. Hosts
// the saved-filter mutation so test pages that never open the
// popover don't need a QueryClient.
function PopoverPanel({
  appliedTree, onApply, pushToast, onQuickFilter, onClose,
}: FilterPopoverProps & { onClose: () => void }) {
  const [tab, setTab] = useState<"new" | "saved" | "quick">("new");
  const [showSaveModal, setShowSaveModal] = useState(false);
  // ``editingFilter`` is the row the user clicked the pencil on. When
  // set, the modal renders in edit mode (pre-fills name + visibility,
  // routes Save through PATCH instead of POST). Cleared on close so
  // the next "Save Filter" click starts fresh.
  const [editingFilter, setEditingFilter] = useState<SavedFilterDto | null>(
    null,
  );

  // Decode the URL tree into UI state on open.
  const initial = useMemo(
    () => wireToUi(appliedTree),
    [appliedTree],
  );
  const [state, setState] = useState<FilterUiState>(initial.state);
  const [flattenedNote, setFlattenedNote] = useState(initial.flattened);
  useEffect(() => {
    setState(initial.state);
    setFlattenedNote(initial.flattened);
  }, [initial.state, initial.flattened]);

  // Debounced auto-apply: whenever the UI state changes and every
  // condition is complete, write the wire tree to the URL.
  const stateRef = useRef(state);
  stateRef.current = state;
  useEffect(() => {
    const incomplete = state.groups.some((g) =>
      g.conditions.some((c) => !isComplete(c)),
    );
    if (incomplete && state.groups.some(
      (g) => g.conditions.length > 0,
    )) {
      return;
    }
    const timer = window.setTimeout(() => {
      const wire = uiToWire(stateRef.current);
      onApply(wire);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [state, onApply]);

  const createMutation = useCreateSavedFilterMutation();
  const updateMutation = useUpdateSavedFilterMutation();

  function reset(): void {
    setState(emptyUiState());
    setFlattenedNote(false);
    onApply(undefined);
  }

  function addGroup(): void {
    setState((prev) => ({
      ...prev,
      groups: [
        ...prev.groups,
        {
          id: makeId(),
          groupOperator: "AND",
          conditions: [],
        },
      ],
    }));
  }

  function updateGroup(next: FilterGroup): void {
    setState((prev) => ({
      ...prev,
      groups: prev.groups.map((g) => (g.id === next.id ? next : g)),
    }));
  }

  function removeGroup(id: string): void {
    setState((prev) => ({
      ...prev,
      groups: prev.groups.filter((g) => g.id !== id),
    }));
  }

  function applySavedFilter(sf: SavedFilterDto): void {
    const decoded = wireToUi(sf.filter_definition as FilterTree);
    setState(decoded.state);
    setFlattenedNote(decoded.flattened);
    onApply(sf.filter_definition as FilterTree);
    onClose();
    pushToast(`Applied ${sf.name}.`, "info");
  }

  function startEditSavedFilter(sf: SavedFilterDto): void {
    // Editing pulls the row's tree into the New Filter tab so the
    // user can tweak conditions before saving. The modal then opens
    // in edit mode for the name + visibility tweaks.
    const decoded = wireToUi(sf.filter_definition as FilterTree);
    setState(decoded.state);
    setFlattenedNote(decoded.flattened);
    setTab("new");
    setEditingFilter(sf);
    setShowSaveModal(true);
  }

  function handleSave(input: {
    name: string;
    visibility: SavedFilterVisibility;
  }): void {
    const wire = uiToWire(state);
    if (wire === undefined) {
      pushToast("Add at least one condition before saving.", "error");
      return;
    }
    if (editingFilter) {
      updateMutation.mutate(
        {
          id: editingFilter.id,
          input: {
            name: input.name,
            filter_definition: wire,
            visibility: input.visibility,
          },
        },
        {
          onSuccess: () => {
            pushToast(`Updated "${input.name}".`, "success");
            setShowSaveModal(false);
            setEditingFilter(null);
            setTab("saved");
          },
          onError: (err) => {
            pushToast(
              messageFor(err) ?? "Couldn't update that filter.",
              "error",
            );
          },
        },
      );
      return;
    }
    createMutation.mutate(
      {
        scope: "commitment",
        name: input.name,
        filter_definition: wire,
        visibility: input.visibility,
      },
      {
        onSuccess: () => {
          pushToast(`Saved "${input.name}".`, "success");
          setShowSaveModal(false);
          setTab("saved");
        },
        onError: (err) => {
          pushToast(
            messageFor(err) ?? "Couldn't save that filter.",
            "error",
          );
        },
      },
    );
  }

  // Count complete conditions for the panel header badge.
  const conditionCount = state.groups.reduce(
    (n, g) => n + g.conditions.filter(isComplete).length,
    0,
  );

  return (
    <>
      <div
        role="dialog"
        aria-label="Advanced filter"
        data-testid="commitments-filter-builder-panel"
        className="absolute left-0 top-full z-20 mt-1 flex w-[44rem] flex-col gap-3 rounded-lg border border-border-subtle bg-raised p-3 shadow-lg"
      >
          <header className="flex items-center justify-between gap-2">
            <div
              role="tablist"
              className="inline-flex rounded-md border border-border-subtle bg-recessed/40 p-0.5"
            >
              <TabButton
                active={tab === "new"}
                testId="commitments-filter-builder-tab-new"
                onClick={() => setTab("new")}
                label="New Filter"
              />
              <TabButton
                active={tab === "saved"}
                testId="commitments-filter-builder-tab-saved"
                onClick={() => setTab("saved")}
                label="Saved Filters"
              />
              <TabButton
                active={tab === "quick"}
                testId="commitments-filter-builder-tab-quick"
                onClick={() => setTab("quick")}
                label="Quick Filters"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="commitments-filter-builder-reset"
                onClick={reset}
                className="text-xs font-medium text-text-tertiary hover:text-text-primary"
              >
                Reset
              </button>
              <button
                type="button"
                data-testid="commitments-filter-builder-save"
                onClick={() => setShowSaveModal(true)}
                disabled={conditionCount === 0}
                className="rounded-md border border-teal/40 px-2 py-1 text-xs font-medium text-teal hover:bg-teal-tint disabled:cursor-not-allowed disabled:opacity-50"
              >
                Save Filter
              </button>
            </div>
          </header>

          {tab === "new" ? (
            <div
              data-testid="commitments-filter-builder-new-tab"
              className="flex flex-col gap-3"
            >
              {flattenedNote && (
                <p className="rounded-md bg-gold-tint/40 px-2 py-1 text-[11px] text-text-secondary">
                  This saved filter is more complex than the builder
                  shows. Editing here will simplify the tree.
                </p>
              )}
              {state.groups.map((g, i) => (
                <div key={g.id} className="flex items-stretch gap-3">
                  <div className="flex w-16 flex-shrink-0 items-start justify-start pt-3">
                    {i === 0 ? (
                      <span className="text-[11px] font-semibold text-text-primary">
                        Where
                      </span>
                    ) : (
                      <button
                        type="button"
                        data-testid="filter-global-op"
                        onClick={() =>
                          setState((prev) => ({
                            ...prev,
                            globalOperator:
                              prev.globalOperator === "AND" ? "OR" : "AND",
                          }))
                        }
                        className="rounded-md border border-border-subtle bg-raised px-2 py-1 text-[11px] font-medium text-text-primary hover:bg-recessed"
                      >
                        {state.globalOperator}
                      </button>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <FilterGroupCard
                      group={g}
                      onChange={updateGroup}
                      onRemove={() => removeGroup(g.id)}
                      canRemove={state.groups.length > 1}
                    />
                  </div>
                </div>
              ))}
              <button
                type="button"
                data-testid="commitments-filter-builder-add-group"
                onClick={addGroup}
                className="ml-16 inline-flex w-fit items-center gap-1 text-[11px] font-medium text-teal hover:text-text-primary"
              >
                <Plus size={12} aria-hidden="true" />
                Add filter
              </button>
            </div>
          ) : tab === "saved" ? (
            <SavedFiltersTab
              onApply={applySavedFilter}
              onEdit={startEditSavedFilter}
            />
          ) : (
            <QuickFiltersTab
              onPick={(key) => {
                onQuickFilter?.(key);
                onClose();
              }}
            />
          )}
      </div>

      <SaveFilterModal
        open={showSaveModal}
        initial={
          editingFilter
            ? {
                name: editingFilter.name,
                visibility: editingFilter.visibility,
              }
            : null
        }
        isPending={
          editingFilter ? updateMutation.isPending : createMutation.isPending
        }
        error={
          editingFilter
            ? updateMutation.isError
              ? (updateMutation.error as Error).message
              : null
            : createMutation.isError
              ? (createMutation.error as Error).message
              : null
        }
        onCancel={() => {
          setShowSaveModal(false);
          setEditingFilter(null);
        }}
        onSave={handleSave}
      />
    </>
  );
}


function messageFor(err: unknown): string | null {
  if (err instanceof Error && err.message) return err.message;
  return null;
}


// Quick Filters tab — small list of one-click presets. FE/BE-043b
// enables the full canonical set (All work / Assigned to me /
// Overdue / Private / Archive).
function QuickFiltersTab({
  onPick,
}: { onPick: (key: QuickFilterKey) => void }) {
  return (
    <div
      data-testid="commitments-filter-builder-quick-tab"
      className="flex flex-col gap-1 text-xs"
    >
      <p className="px-2 pt-1 text-[11px] uppercase tracking-wide text-text-tertiary">
        Common views
      </p>
      <QuickFilterButton
        testId="quick-filter-all-work"
        label="All work"
        hint="Clear active filters"
        onClick={() => onPick("all_work")}
      />
      <QuickFilterButton
        testId="quick-filter-assigned-to-me"
        label="Assigned to me"
        hint="Only Commitments you own"
        onClick={() => onPick("assigned_to_me")}
      />
      <QuickFilterButton
        testId="quick-filter-overdue"
        label="Overdue"
        hint="Past due date and still open"
        onClick={() => onPick("overdue")}
      />
      <QuickFilterButton
        testId="quick-filter-private"
        label="Private"
        hint="Only Commitments marked private"
        onClick={() => onPick("private")}
      />
      <QuickFilterButton
        testId="quick-filter-archive"
        label="Archive"
        hint="Show archived Commitments"
        onClick={() => onPick("archive")}
      />
    </div>
  );
}


function QuickFilterButton({
  testId, label, hint, onClick, disabled,
}: {
  testId: string;
  label: string;
  hint?: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      title={hint}
      className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs text-text-primary hover:bg-recessed disabled:cursor-not-allowed disabled:text-text-tertiary"
    >
      <span>{label}</span>
      {hint && (
        <span className="text-[10px] text-text-tertiary">{hint}</span>
      )}
    </button>
  );
}


function TabButton({
  active, testId, onClick, label,
}: {
  active: boolean;
  testId: string;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={testId}
      onClick={onClick}
      className={[
        "rounded-md px-2.5 py-1 text-xs font-medium transition-fast ease-out",
        active
          ? "bg-raised text-text-primary"
          : "text-text-secondary hover:text-text-primary",
      ].join(" ")}
    >
      {label}
    </button>
  );
}
